import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Brain,
  Trophy,
  Clock,
  BarChart2,
  XCircle,
  CheckCircle,
} from "lucide-react";
import { useAuth } from "../../hooks/AuthenticationProvider";
import { useLocation, useNavigate } from "react-router-dom";
import PvPLobby from "../../components/PvPLobby";
import MultiplayerGame from "../../components/MultiplayerGame";

const SpanTaskMultiWrapper = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showLobby, setShowLobby] = useState(true);
  const [activeMatch, setActiveMatch] = useState(null);

  useEffect(() => {
    // Check if we have a match ID in URL
    const searchParams = new URLSearchParams(location.search);
    const matchId = searchParams.get("matchId");

    if (matchId) {
      setActiveMatch(matchId);
      setShowLobby(false);
    }
  }, [location]);

  const handleMatchCreated = (matchId) => {
    setActiveMatch(matchId);
    setShowLobby(false);
  };

  return (
    <div className="h-full">
      {showLobby ? (
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
          <PvPLobby
            game="span-task"
            onMatchCreated={handleMatchCreated}
            onLeaveLobby={() => navigate("/gameroom")}
            gamesData={[{ id: "span-task", name: "Memory Span" }]}
          />
        </div>
      ) : (
        <MultiplayerGame
          matchId={activeMatch}
          gameComponent={SpanTaskMulti} // This is your existing component
          onExit={() => navigate("/gameroom")}
        />
      )}
    </div>
  );
};

const SpanTaskMulti = ({
  gameState,
  updateGameState,
  submitScore,
  opponent,
}) => {
  const { user } = useAuth();

  // ----------------------------------
  //           GAME STATES
  // ----------------------------------
  const [gameMode, setGameMode] = useState("play"); // "play" | "results"
  const [level, setLevel] = useState(1);
  const [gameStarted, setGameStarted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [timeLeft, setTimeLeft] = useState(180);
  const timerRef = useRef(null);
  const [waitingForOpponent, setWaitingForOpponent] = useState(false);
  const [gameOverHandled, setGameOverHandled] = useState(false);
  const [scoreSubmitted, setScoreSubmitted] = useState(false);

  // Countdown states
  const [countdown, setCountdown] = useState(null);
  const [showCountdown, setShowCountdown] = useState(false);
  const countdownRef = useRef(null);

  // Performance metrics and scoring
  const [score, setScore] = useState(0);
  const [strikes, setStrikes] = useState(0);
  const [correctResponses, setCorrectResponses] = useState(0);
  const [incorrectResponses, setIncorrectResponses] = useState(0);
  const [reactionTimes, setReactionTimes] = useState([]);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackCorrect, setFeedbackCorrect] = useState(false);

  // Sequence state
  const matrixSize = 3; // 3x3 grid
  const numSquares = matrixSize * matrixSize;
  const [sequenceLength, setSequenceLength] = useState(3);
  const [currentSequence, setCurrentSequence] = useState([]);
  const [displayIndex, setDisplayIndex] = useState(0);
  const [isShowingSequence, setIsShowingSequence] = useState(false);
  const [userInputIndex, setUserInputIndex] = useState(0);
  const [activeSquare, setActiveSquare] = useState(null);

  // Refs
  const lastRevealTime = useRef(Date.now());

  // Theme references
  const primaryColor = "from-blue-500 to-cyan-600";
  const primaryColorSolid = "bg-blue-500";
  const secondaryColor = "text-blue-300";

  // ----------------------------------
  //        MULTIPLAYER STATE
  // ----------------------------------
  useEffect(() => {
    if (gameState) {
      // If we're waiting for both players to be ready
      if (gameState.status === "pending") {
        setGameMode("play");
        setGameStarted(false);
        setWaitingForOpponent(false); // Reset waiting state
      }
      // If the game is getting ready to start (both players clicked ready)
      else if (gameState.status === "ready") {
        if (!gameStarted && !showCountdown) {
          // Both players are ready, start the countdown
          startGame();
        }
      }
      // If the game is active, make sure we're playing
      else if (gameState.status === "active") {
        if (!gameStarted) {
          setGameStarted(true);
          startGame();
        }
      }
      // If the game is completed, show results
      else if (gameState.status === "completed") {
        if (isPlaying) {
          endGame();
        }
      }

      // Check if opponent is ready (for UI indication)
      if (gameState.playerReady && opponent) {
        const opponentReady = gameState.playerReady[opponent.email] || false;
        console.log(`Opponent ready: ${opponentReady}`);
      }
    }
  }, [gameState, gameStarted, isPlaying, opponent, showCountdown]);

  useEffect(() => {
    if (gameState?.status === "completed" && !gameOverHandled) {
      endGame();
      setGameOverHandled(true);
    }
  }, [gameState?.status, gameOverHandled]);

  // Check if all players are ready
  useEffect(() => {
    if (!gameState || !opponent || !user?.email) return;

    // Check if all players are ready
    const playerReadyState = gameState.playerReady || {};
    const allPlayersReady =
      playerReadyState[user.email] && playerReadyState[opponent.email];

    // If all players are ready but game is still pending, transition to "ready" state
    if (allPlayersReady && gameState.status === "pending" && updateGameState) {
      updateGameState({
        status: "ready",
      });
    }
  }, [
    gameState?.playerReady,
    opponent,
    user?.email,
    updateGameState,
    gameState?.status,
  ]);

  // ----------------------------------
  //        GAME & TIMER LOGIC
  // ----------------------------------
  useEffect(() => {
    if (isPlaying) {
      timerRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            endGame();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [isPlaying]);

  // Generate a random sequence
  const generateSequence = useCallback(
    (length) => {
      const seq = [];
      for (let i = 0; i < length; i++) {
        seq.push(Math.floor(Math.random() * numSquares));
      }
      return seq;
    },
    [numSquares]
  );

  // Start the game with countdown
  const startGame = () => {
    setShowCountdown(true);
    setCountdown(3);

    countdownRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          startGameAfterCountdown();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Start the actual game after countdown
  const startGameAfterCountdown = () => {
    setShowCountdown(false);

    // Reset all game states
    setScore(0);
    setCorrectResponses(0);
    setIncorrectResponses(0);
    setStrikes(0);
    setReactionTimes([]);

    // Get level from gameState if available
    if (gameState && gameState.gameLevel) {
      setLevel(gameState.gameLevel);
    }

    setSequenceLength(2 + level); // base difficulty
    setTimeLeft(180);
    setIsPlaying(true);

    // Generate the initial sequence
    const seq = generateSequence(2 + level);
    setCurrentSequence(seq);
    setDisplayIndex(0);
    setIsShowingSequence(true);
    setUserInputIndex(0);

    // Update the game state
    if (updateGameState) {
      updateGameState({
        status: "active",
        playerReady: true,
        myProgress: 0,
        myScore: 0,
      });
    }

    // Reveal the sequence
    revealSequence(seq);
  };

  /**
   * This function reveals the sequence one item at a time, turning each square on, then off,
   * even if the next item is the same square. That way repeated squares blink separately.
   */
  const revealSequence = (sequence, idx = 0) => {
    // If we've shown the entire sequence, stop and let user respond
    if (idx >= sequence.length) {
      setIsShowingSequence(false);
      setActiveSquare(null);
      lastRevealTime.current = Date.now();
      return;
    }

    // Highlight this item
    setActiveSquare(sequence[idx]);

    // Show it for 600 ms
    setTimeout(() => {
      // Turn off
      setActiveSquare(null);

      // Then wait 300 ms before going to next item
      setTimeout(() => {
        revealSequence(sequence, idx + 1);
      }, 300);
    }, 600);
  };

  // Handler for clicking on squares
  const handleSquareClick = (index) => {
    // If the sequence is still being revealed, ignore clicks
    if (isShowingSequence) return;

    // Record reaction time
    const reaction = Date.now() - lastRevealTime.current;
    setReactionTimes((prev) => [...prev, reaction]);

    // Check if user clicked the correct next square in the sequence
    if (currentSequence[userInputIndex] === index) {
      // Correct
      setScore((prev) => prev + 10);
      setCorrectResponses((prev) => prev + 1);

      // Update game state
      if (updateGameState) {
        updateGameState({
          myProgress: (sequenceLength - 2) / 13, // Assuming max length is 15 (i.e., 13 increases from initial 2)
          myScore: score + 10,
          myLastResponse: {
            correct: true,
            points: 10,
            responseTime: reaction,
          },
        });
      }

      // Show correct feedback
      setFeedbackCorrect(true);
      setShowFeedback(true);
      setTimeout(() => setShowFeedback(false), 300);

      // Move to next item in the sequence
      if (userInputIndex + 1 === currentSequence.length) {
        // The user recalled the entire sequence. Generate next round
        setTimeout(() => {
          nextRound();
        }, 700);
      } else {
        setUserInputIndex((prev) => prev + 1);
      }
    } else {
      // Incorrect
      setScore((prev) => Math.max(0, prev - 5));
      setIncorrectResponses((prev) => prev + 1);

      // Update game state
      if (updateGameState) {
        updateGameState({
          myProgress: (sequenceLength - 2) / 13,
          myScore: Math.max(0, score - 5),
          myLastResponse: {
            correct: false,
            points: -5,
            responseTime: reaction,
          },
        });
      }

      // Update strikes
      setStrikes((prevStrikes) => {
        const newStrikes = prevStrikes + 1;

        if (newStrikes >= 3) {
          // On third strike, end game
          setTimeout(() => {
            endGame();
          }, 500);
        } else {
          // Otherwise, generate a NEW sequence of the same length
          const sameLength = currentSequence.length;
          const newSeq = generateSequence(sameLength);

          setTimeout(() => {
            setCurrentSequence(newSeq);
            setUserInputIndex(0);
            setDisplayIndex(0);
            setIsShowingSequence(true);
            revealSequence(newSeq);
          }, 500);
        }

        return newStrikes;
      });

      // Show incorrect feedback
      setFeedbackCorrect(false);
      setShowFeedback(true);
      setTimeout(() => setShowFeedback(false), 500);
    }
  };

  // Move to the next round with a longer sequence
  const nextRound = () => {
    // Increase length by 1
    const newLength = sequenceLength + 1;
    setSequenceLength(newLength);

    const newSeq = generateSequence(newLength);
    setCurrentSequence(newSeq);
    setUserInputIndex(0);

    setDisplayIndex(0);
    setIsShowingSequence(true);

    revealSequence(newSeq);
  };

  // End the game
  const endGame = () => {
    if (scoreSubmitted) return;
    setScoreSubmitted(true);

    setIsPlaying(false);
    setGameMode("results");

    // Calculate average reaction time
    const avgReactionTime =
      reactionTimes.length > 0
        ? reactionTimes.reduce((a, b) => a + b, 0) / reactionTimes.length
        : 0;

    // Determine longest sequence
    const longestSequence =
      sequenceLength > currentSequence.length
        ? sequenceLength - 1 // Because we increased it already when the user completed the sequence
        : currentSequence.length;

    // Submit the score + detailed stats
    if (submitScore) {
      // Create a stats object with all the performance metrics
      const stats = {
        correctResponses,
        incorrectResponses,
        averageReactionTime: Math.round(avgReactionTime),
        longestSequence,
        level,
        timestamp: new Date(),
      };

      // Submit both score and stats
      submitScore(score, stats);

      // Also update the game state one last time with final score and progress
      if (updateGameState) {
        updateGameState({
          myProgress: 1, // 100% progress
          myScore: score,
          myLastResponse: {
            gameComplete: true,
            finalStats: stats,
          },
        });
      }
    }
  };

  // Format time as M:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  // Handler for the Ready to Play button
  const handleReadyClick = () => {
    console.log("Ready to Play clicked");
    setWaitingForOpponent(true);

    // Update the game state to indicate this player is ready
    if (updateGameState) {
      updateGameState({
        playerReady: {
          ...(gameState?.playerReady || {}),
          [user.email]: true,
        },
        // Add player device/connection info
        playerInfo: {
          ...(gameState?.playerInfo || {}),
          [user.email]: {
            device: navigator.userAgent,
            connectionTime: new Date().toISOString(),
            screenWidth: window.innerWidth,
            screenHeight: window.innerHeight,
          },
        },
      });
    }
  };

  useEffect(() => {
    // Check if the opponent has quit
    if (gameState?.playerQuit && gameState.playerQuit !== user?.email) {
      // If we're still playing, end the game automatically with a win
      if (isPlaying) {
        endGame();
        // You could optionally show a special message:
        alert("Your opponent has left the game. You win by forfeit!");
      }
    }
  }, [gameState?.playerQuit, user?.email, isPlaying]);

  // ----------------------------------
  //            RENDER
  // ----------------------------------
  return (
    <div className="w-full text-white p-0">
      <div className="w-full">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div
            className={`font-medium flex items-center gap-2 ${secondaryColor}`}
          >
            <Brain size={18} className="text-blue-400" />
            <span>Memory Span Challenge</span>
          </div>
        </div>

        {/* Game Container */}
        <div className="bg-slate-800 rounded-xl overflow-hidden">
          {/* Play Screen */}
          {gameMode === "play" && (
            <div className="relative p-6 md:p-8">
              {/* Start button or waiting indicator */}
              {!gameStarted && (
                <div className="absolute inset-0 z-50 flex items-center justify-center bg-slate-900/80">
                  {waitingForOpponent ? (
                    <div className="flex flex-col items-center gap-4">
                      <div className="w-16 h-16 border-4 border-t-transparent border-blue-500 rounded-full animate-spin"></div>
                      <p className="text-white text-xl">
                        Waiting for opponent...
                      </p>
                    </div>
                  ) : (
                    <button
                      onClick={handleReadyClick}
                      className="py-3 px-6 bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 text-white rounded-lg"
                    >
                      Ready to Play
                    </button>
                  )}
                </div>
              )}

              {/* Countdown overlay */}
              {showCountdown && (
                <div className="absolute inset-0 z-50 flex items-center justify-center bg-slate-900/80">
                  <div className="flex flex-col items-center">
                    <div className="text-6xl font-bold text-white mb-4">
                      {countdown > 0 ? countdown : "GO!"}
                    </div>
                    <div className="text-xl text-slate-300">Get ready...</div>
                  </div>
                </div>
              )}

              {/* Game stats header */}
              <div className="flex justify-between items-center mb-6">
                <div className="bg-gradient-to-r from-blue-500/20 to-cyan-600/20 px-4 py-2 rounded-lg">
                  <span className="text-blue-300 font-medium">
                    Level: {level}
                  </span>
                </div>

                <div className="flex items-center">
                  <div className="bg-slate-700/50 px-4 py-2 rounded-lg flex items-center gap-2">
                    <Clock size={18} className="text-slate-400" />
                    <span className="text-white font-mono">
                      {formatTime(timeLeft)}
                    </span>
                  </div>
                  <div className="ml-4 bg-gradient-to-r from-blue-500/20 to-cyan-600/20 px-4 py-2 rounded-lg">
                    <span className="text-white font-medium">
                      Score: {score}
                    </span>
                  </div>
                </div>
              </div>

              {/* Opponent progress */}
              {gameState && opponent && (
                <div className="mb-4 bg-slate-800/60 p-3 rounded-lg">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm text-slate-300">
                      {opponent.name}'s Progress
                    </span>
                    <span className="text-xs text-white">
                      {gameState?.progress && gameState.progress[opponent.email]
                        ? `${Math.round(
                            gameState.progress[opponent.email] * 100
                          )}%`
                        : "0%"}
                    </span>
                  </div>
                  <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
                    <div
                      className="bg-blue-500 h-full rounded-full"
                      style={{
                        width: `${
                          ((gameState?.progress &&
                            gameState.progress[opponent.email]) ||
                            0) * 100
                        }%`,
                      }}
                    ></div>
                  </div>
                  {gameState?.scores &&
                    gameState.scores[opponent.email] !== undefined && (
                      <div className="mt-1 text-right text-xs text-blue-300">
                        Score: {gameState.scores[opponent.email]}
                      </div>
                    )}
                </div>
              )}

              {/* Strike Indicators */}
              <div className="flex justify-center mb-4">
                {[...Array(3)].map((_, idx) => {
                  const isStrike = idx < strikes;
                  return (
                    <div
                      key={idx}
                      className={`mx-1 p-2 rounded-full ${
                        isStrike
                          ? "bg-red-500/30 text-red-400"
                          : "bg-slate-700/20 text-slate-500"
                      }`}
                    >
                      <XCircle size={24} />
                    </div>
                  );
                })}
              </div>

              {/* Game content */}
              <div className="flex flex-col items-center justify-center">
                {/* Sequence Length Indicator */}
                <div className="mb-4 text-center">
                  <h3 className="text-white font-medium">
                    {isShowingSequence
                      ? "Watch the sequence..."
                      : "Repeat the sequence"}
                  </h3>
                  <p className="text-sm text-slate-400">
                    Sequence length: {currentSequence.length}
                  </p>
                </div>

                {/* Memory Grid */}
                <div className="relative mb-8">
                  <div className="grid grid-cols-3 gap-3 bg-slate-900/60 p-5 rounded-xl border border-slate-700">
                    {[...Array(numSquares)].map((_, index) => (
                      <div
                        key={index}
                        onClick={() => handleSquareClick(index)}
                        className={`w-16 h-16 md:w-20 md:h-20 rounded-lg border-2 transition-colors ${
                          isShowingSequence
                            ? // If we are still revealing, highlight the active square
                              activeSquare === index
                              ? `${primaryColorSolid} border-blue-400`
                              : "bg-slate-800/60 border-slate-700"
                            : // If reveal is done, squares are clickable
                            activeSquare === index
                            ? `${primaryColorSolid} border-blue-400`
                            : "bg-slate-800/60 border-slate-700 hover:border-slate-600 cursor-pointer"
                        }`}
                      ></div>
                    ))}
                  </div>

                  {/* Feedback Overlay */}
                  <AnimatePresence>
                    {showFeedback && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        transition={{ duration: 0.2 }}
                        className="absolute inset-0 flex items-center justify-center"
                      >
                        <div
                          className={`p-6 rounded-full ${
                            feedbackCorrect
                              ? "bg-green-500/20 text-green-400"
                              : "bg-red-500/20 text-red-400"
                          }`}
                        >
                          {feedbackCorrect ? (
                            <CheckCircle size={60} />
                          ) : (
                            <XCircle size={60} />
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Progress indicator */}
                <div className="w-full max-w-md bg-slate-800/50 border border-slate-700 rounded-lg p-4 mb-6">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-slate-400 text-sm">
                      Sequence Progress
                    </span>
                    <span className="text-white font-medium">
                      {userInputIndex}/{currentSequence.length}
                    </span>
                  </div>
                  <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-blue-500 to-cyan-600"
                      style={{
                        width: `${
                          (userInputIndex / currentSequence.length) * 100
                        }%`,
                      }}
                    ></div>
                  </div>
                </div>

                {/* Instructions reminder */}
                <div className="mt-2 bg-slate-800/50 border border-slate-700 rounded-lg p-3 text-sm text-slate-300 max-w-md text-center">
                  {isShowingSequence
                    ? "Watch carefully! Remember the order of the highlighted squares."
                    : "Click the squares in the exact order they appeared."}
                </div>
              </div>
            </div>
          )}

          {/* Results Screen */}
          {gameMode === "results" && (
            <div className="p-6 md:p-8">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-full mx-auto flex items-center justify-center mb-4">
                  <Trophy size={40} className="text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">
                  Game Complete!
                </h2>
                <p className="text-slate-300">
                  You scored {score} points in your multiplayer match!
                </p>
              </div>

              {/* Match results comparison */}
              {gameState && gameState.status === "completed" && opponent && (
                <div className="bg-slate-800/70 border border-slate-700 rounded-xl p-4 mb-6">
                  <h3 className="text-lg font-bold text-white text-center mb-4">
                    Match Results
                  </h3>

                  <div className="flex justify-between items-center mb-6">
                    <div className="text-center flex-1 p-3">
                      <div className="text-lg font-medium text-white">
                        {user?.firstName || user?.email?.split("@")[0]}
                      </div>
                      <div className="text-3xl font-bold text-blue-300 mt-2">
                        {score}
                      </div>
                      <div className="text-sm text-slate-400 mt-1">points</div>
                    </div>

                    <div className="flex flex-col items-center px-4">
                      <div className="text-xl font-bold text-white">VS</div>
                      <div className="w-px h-16 bg-slate-600 my-2"></div>
                    </div>

                    <div className="text-center flex-1 p-3">
                      <div className="text-lg font-medium text-white">
                        {opponent.name}
                      </div>
                      <div className="text-3xl font-bold text-blue-300 mt-2">
                        {gameState.scores?.[opponent.email] || 0}
                      </div>
                      <div className="text-sm text-slate-400 mt-1">points</div>
                    </div>
                  </div>

                  <div className="flex justify-center">
                    {score > (gameState.scores?.[opponent.email] || 0) ? (
                      <div className="bg-emerald-500/20 border border-emerald-500/30 text-emerald-300 py-2 px-4 rounded-lg font-medium">
                        You Won! 🏆
                      </div>
                    ) : score === (gameState.scores?.[opponent.email] || 0) ? (
                      <div className="bg-amber-500/20 border border-amber-500/30 text-amber-300 py-2 px-4 rounded-lg font-medium">
                        It's a Tie! 🤝
                      </div>
                    ) : (
                      <div className="bg-blue-500/20 border border-blue-500/30 text-blue-300 py-2 px-4 rounded-lg font-medium">
                        {opponent.name} Won! Better luck next time!
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Explanation box */}
              <div className="mt-2 p-3 bg-slate-800/60 rounded-lg max-w-xl mx-auto mb-2">
                <p className="text-sm text-slate-300">
                  <span className="font-medium text-blue-300">Memory Span</span>
                  : Measures how many items you can hold in your working memory.
                  <br />
                  <span className="font-medium text-blue-300">
                    Longest Sequence
                  </span>
                  : The longest sequence you successfully recalled.
                  <br />
                  <span className="font-medium text-blue-300">
                    Reaction Time
                  </span>
                  : How quickly you responded to each item in the sequence.
                </p>
              </div>

              {/* Performance stats */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-5">
                  <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                    <Trophy size={20} className="text-blue-300" />
                    Your Results
                  </h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center pb-2 border-b border-slate-700">
                      <span className="text-slate-300">Final Score</span>
                      <span className="text-2xl font-bold text-blue-300">
                        {score}
                      </span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Difficulty</span>
                      <span className="text-white font-medium">{level}</span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Longest Sequence</span>
                      <span className="text-white">
                        {sequenceLength > currentSequence.length
                          ? sequenceLength - 1
                          : currentSequence.length}
                      </span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Correct Responses</span>
                      <span className="text-green-400">{correctResponses}</span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">
                        Incorrect Responses
                      </span>
                      <span className="text-red-400">{incorrectResponses}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-5">
                  <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                    <BarChart2 size={20} className="text-blue-300" />
                    Performance Analysis
                  </h3>
                  <div className="space-y-5">
                    {/* Accuracy bar */}
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-slate-300 text-sm">Accuracy</span>
                        <span className="text-white text-sm">
                          {correctResponses + incorrectResponses > 0
                            ? `${Math.round(
                                (correctResponses /
                                  (correctResponses + incorrectResponses)) *
                                  100
                              )}%`
                            : "N/A"}
                        </span>
                      </div>
                      <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-blue-500 to-cyan-600"
                          style={{
                            width:
                              correctResponses + incorrectResponses > 0
                                ? `${
                                    (correctResponses /
                                      (correctResponses + incorrectResponses)) *
                                    100
                                  }%`
                                : "0%",
                          }}
                        ></div>
                      </div>
                    </div>

                    <div className="flex justify-between text-sm text-slate-300">
                      <span>Average Response Time</span>
                      <span>
                        {reactionTimes.length > 0
                          ? `${Math.round(
                              reactionTimes.reduce((a, b) => a + b, 0) /
                                reactionTimes.length
                            )} ms`
                          : "N/A"}
                      </span>
                    </div>

                    <div className="flex justify-between text-sm text-slate-300 mb-2">
                      <span>Fastest Response</span>
                      <span>
                        {reactionTimes.length > 0
                          ? `${Math.min(...reactionTimes)} ms`
                          : "N/A"}
                      </span>
                    </div>

                    <div className="mt-4">
                      <p className="text-slate-300 text-sm leading-relaxed">
                        Working memory is crucial for many cognitive tasks. By
                        practicing memory span exercises regularly, you can
                        improve your ability to hold and manipulate information
                        in the short term.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Return to lobby button */}
              <div className="flex justify-center">
                <button
                  onClick={() => submitScore(score)}
                  className="py-3 px-6 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                >
                  Return to Lobby
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SpanTaskMultiWrapper;
